// ─── Language URLs ────────────────────────────────────────────────────────────
// The URL decides the language: English at the root, the others under a prefix
// (/de/, /fr/, /it/). Crawlers that do not run JavaScript only ever see the HTML
// react-snap wrote for a URL, so every language needs its own URL to be indexed
// at all. The table lives in config/site.json because tools/seo/sitemap.js reads
// it too. Identical in all five DRIVE apps.

import site from "../config/site.json";

export interface SiteLanguage {
  /** i18next code, also the folder under public/locales ("gb" is English). */
  code: string;
  /** URL prefix without a trailing slash; "" for the root language. */
  prefix: string;
  hreflang: string;
  ogLocale: string;
}

export const LANGUAGES: SiteLanguage[] = site.languages;
export const DEFAULT_LANGUAGE = LANGUAGES[0];

/** Set by the localStorage key the old i18next detector cached, so returning visitors keep their language. */
const STORAGE_KEY = "i18nextLng";

export function langFromPath(pathname: string): SiteLanguage {
  const first = `/${(pathname.split("/")[1] ?? "").toLowerCase()}`;
  return LANGUAGES.find((lang) => lang.prefix !== "" && lang.prefix === first) ?? DEFAULT_LANGUAGE;
}

/** The path without its language prefix: "/de/account" -> "/account", "/de" -> "/". */
export function stripLang(pathname: string): string {
  const { prefix } = langFromPath(pathname);
  const rest = prefix ? pathname.slice(prefix.length) : pathname;
  return rest.startsWith("/") ? rest : `/${rest}`;
}

/** The same page in another language: localizedPath("/account", "de") -> "/de/account". */
export function localizedPath(pathname: string, code: string): string {
  const lang = LANGUAGES.find((l) => l.code === code) ?? DEFAULT_LANGUAGE;
  return `${lang.prefix}${stripLang(pathname)}`;
}

function matchLanguage(value: string | null | undefined): SiteLanguage | undefined {
  const base = value?.split("-")[0].toLowerCase();
  return LANGUAGES.find((lang) => lang.code === base);
}

/**
 * The language a visitor asked for: an explicit earlier choice first, then the
 * browser. undefined when neither names one of ours, which keeps them on English.
 */
export function preferredLanguage(): SiteLanguage | undefined {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // storage blocked: fall through to the browser language
  }
  if (stored) return matchLanguage(stored);
  return matchLanguage(window.navigator.language);
}

export function rememberLanguage(code: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // storage blocked: the choice only lasts for this page view
  }
}

/** react-snap's headless Chrome identifies itself with this user agent. */
export function isPrerender(): boolean {
  return window.navigator.userAgent === "ReactSnap";
}
